import React, { useState, useEffect } from 'react';
import { useOKR } from '../contexts/OKRContext';
import LoadingSpinner from './LoadingSpinner';

const OKRForm = ({ onSuccess, onCancel, initialData = null }) => {
  const { cycles, createObjective, createKeyResult, loading, error, clearError } = useOKR();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: 'individual',
    cycle_id: '',
    parent_objective_id: '',
    linked_goal_ids: [],
    assigned_to: []
  });

  const [keyResultsData, setKeyResultsData] = useState([
    {
      title: '',
      description: '',
      type: 'numeric',
      target_value: '',
      unit: '',
      is_completed: false
    }
  ]);

  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const levels = [
    { value: 'individual', label: 'Individual' },
    { value: 'team', label: 'Team' },
    { value: 'department', label: 'Department' },
    { value: 'organization', label: 'Organization' }
  ];

  const keyResultTypes = [
    { value: 'numeric', label: 'Numeric (0 to target)' },
    { value: 'percentage', label: 'Percentage (0% to 100%)' },
    { value: 'boolean', label: 'Boolean (Done/Not Done)' },
    { value: 'milestone', label: 'Milestone (Completed out of total)' }
  ];

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (error) {
      setFormErrors({ general: error });
    }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific field error
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleKeyResultChange = (index, field, value) => {
    setKeyResultsData(prev => prev.map((kr, i) => 
      i === index ? { ...kr, [field]: value } : kr
    ));
  };

  const addKeyResult = () => {
    setKeyResultsData(prev => [...prev, {
      title: '',
      description: '',
      type: 'numeric',
      target_value: '',
      unit: '',
      is_completed: false
    }]);
  };

  const removeKeyResult = (index) => {
    if (keyResultsData.length > 1) {
      setKeyResultsData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Objective title is required';
    }

    if (!formData.cycle_id) {
      errors.cycle_id = 'Please select an OKR cycle';
    }

    // Validate key results
    keyResultsData.forEach((kr, index) => {
      if (!kr.title.trim()) {
        errors[`keyResult_${index}_title`] = 'Key result title is required';
      }
      
      if (kr.type !== 'boolean' && !kr.target_value) {
        errors[`keyResult_${index}_target`] = 'Target value is required';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);
    clearError();

    try {
      // Create objective first
      const objectiveData = {
        ...formData,
        assigned_to: formData.assigned_to.filter(id => id) // Remove empty strings
      };

      const createdObjective = await createObjective(objectiveData);

      // Create key results
      const keyResultPromises = keyResultsData.map(kr => 
        createKeyResult({
          ...kr,
          objective_id: createdObjective.id,
          target_value: kr.type === 'boolean' ? null : parseFloat(kr.target_value) || null
        })
      );

      await Promise.all(keyResultPromises);

      if (onSuccess) {
        onSuccess(createdObjective);
      }
    } catch (error) {
      console.error('Error creating OKR:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const activeCycles = cycles.filter(cycle => ['planning', 'active'].includes(cycle.status));

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {initialData ? 'Edit Objective & Key Results' : 'Create New OKR'}
        </h2>
        <p className="text-gray-600">
          Define an objective with measurable key results to track progress.
        </p>
      </div>

      {formErrors.general && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{formErrors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Objective Details */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Objective Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Objective Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Increase customer satisfaction"
              />
              {formErrors.title && (
                <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OKR Cycle *
              </label>
              <select
                name="cycle_id"
                value={formData.cycle_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.cycle_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select a cycle</option>
                {activeCycles.map(cycle => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name} ({cycle.cycle_type}) - {cycle.status}
                  </option>
                ))}
              </select>
              {formErrors.cycle_id && (
                <p className="text-red-500 text-sm mt-1">{formErrors.cycle_id}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level
              </label>
              <select
                name="level"
                value={formData.level}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {levels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe what you want to achieve..."
            />
          </div>
        </div>

        {/* Key Results */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Key Results</h3>
            <button
              type="button"
              onClick={addKeyResult}
              className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm"
            >
              + Add Key Result
            </button>
          </div>

          {keyResultsData.map((keyResult, index) => (
            <div key={index} className="bg-white p-4 rounded-lg mb-4 border">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-gray-900">Key Result {index + 1}</h4>
                {keyResultsData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeKeyResult(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={keyResult.title}
                    onChange={(e) => handleKeyResultChange(index, 'title', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors[`keyResult_${index}_title`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Achieve 95% customer satisfaction score"
                  />
                  {formErrors[`keyResult_${index}_title`] && (
                    <p className="text-red-500 text-sm mt-1">{formErrors[`keyResult_${index}_title`]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={keyResult.type}
                    onChange={(e) => handleKeyResultChange(index, 'type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {keyResultTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {keyResult.type !== 'boolean' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Value *
                      </label>
                      <input
                        type="number"
                        value={keyResult.target_value}
                        onChange={(e) => handleKeyResultChange(index, 'target_value', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors[`keyResult_${index}_target`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="100"
                        step="0.01"
                      />
                      {formErrors[`keyResult_${index}_target`] && (
                        <p className="text-red-500 text-sm mt-1">{formErrors[`keyResult_${index}_target`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={keyResult.unit}
                        onChange={(e) => handleKeyResultChange(index, 'unit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., %, points, users"
                      />
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={keyResult.description}
                    onChange={(e) => handleKeyResultChange(index, 'description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe how this will be measured..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-200"
            disabled={submitLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-50"
          >
            {submitLoading ? (
              <div className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">Creating OKR...</span>
              </div>
            ) : (
              initialData ? 'Update OKR' : 'Create OKR'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OKRForm;